import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import LaunchIcon from '@mui/icons-material/Launch';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import { IconButton, Stack, Typography } from '@mui/material';
import classnames from 'classnames';
import React, { memo, useContext, useMemo, useRef, useState } from 'react';
import { ReactComponent as LinkedInIcon } from '../../../../../assets/icons/linkedin.svg';
import { AuthContext } from '../../../../../contexts/AuthContext';
import { WorkspacesContext } from '../../../../../contexts/WorkspacesContext';
import useBackwardsCompatible from '../../../../../hooks/useBackwardsCompatible';
import useRoute from '../../../../../hooks/useRoute';
import theme from '../../../../../theme';
import { Dashboard, DashboardQuerySource } from '../../../../../types/files';
import SelectDealSourceModal from '../../../../modals/home-table-view/SelectDealSourceModal';
import DealSourceModal from '../../../../modals/tables/dashboards/rows/DealSourceModal';
import useStyles from "../styles";

const SourceCell: React.FC<{ dashboard: Dashboard }> = ({ dashboard }) => {
    const classes = useStyles();
    const { userGroup } = useContext(AuthContext);
    const { workspaces } = useContext(WorkspacesContext);
    const { dashboardSource } = useBackwardsCompatible();
    const { redirectToWorkspace } = useRoute();
    const [fieldHover, setFieldHover] = useState<boolean>(false);
    const [editModalOpen, setEditModalOpen] = useState<boolean>(false);
    const [viewModalOpen, setViewModalOpen] = useState<boolean>(false);
    const dealSourceRef = useRef<HTMLElement>(null);

    const source = useMemo(() => dashboardSource(dashboard), [dashboard, dashboardSource]);

    const workspace = useMemo(() => workspaces.find(ws =>
        ws?.name?.toLowerCase() === source?.name?.toLowerCase()
        && ws?.email?.toLowerCase() === source?.email?.toLowerCase()
    ), [source, workspaces]);

    return (<>
        <Stack width="100%" ref={dealSourceRef} onMouseLeave={() => setFieldHover(false)}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" width="100%"
                onMouseEnter={() => setFieldHover(!!source?.name && source?.name !== DashboardQuerySource.LINKEDIN)}>
                <Stack direction="row" spacing={1} alignItems="flex-start" justifyContent="flex-start">
                    {(source?.name === DashboardQuerySource.LINKEDIN) ? (<>
                        <LinkedInIcon className={classes.icon} fill={'#7bd4d4'} />
                        <Typography className={classnames(classes.label, classes.sublabel)}>
                            {'Added from LinkedIn'}
                        </Typography>
                    </>) : (!!source?.name || !!source?.email) ? (<>
                        <PersonOutlineOutlinedIcon sx={{ width: 24, height: 24, fill: "rgb(163, 167, 172)" }} />
                        <Stack direction="column" alignItems="flex-start" justifyContent="center">
                            {!!source?.name && (
                                <Typography className={classes.label}
                                    onClick={() => !!workspace ? redirectToWorkspace(workspace!.id, userGroup) : undefined}
                                    sx={{ cursor: !!workspace ? 'pointer' : 'auto', "&:hover": {textDecoration: 'underline'}}}>
                                    {source?.name}
                                </Typography>
                            )}
                        </Stack>
                    </>) : (
                        <Typography className={classnames(classes.label, classes.sublabel)} pl={4}>
                            {'No deal source'}
                        </Typography>
                    )}
                </Stack>
                {fieldHover && (
                    <Stack direction="row" spacing={1} alignItems="stretch" justifyContent="flex-end">
                        <IconButton disableRipple
                            onClick={() => setEditModalOpen(true)}
                            sx={{ padding: 'unset !important'}}>
                            <EditOutlinedIcon className={classes.icon} sx={{ fill: theme.colors.neutral['500'] }} />
                        </IconButton>
                        <IconButton disableRipple
                            onClick={() => setViewModalOpen(true)}
                            sx={{ padding: 'unset !important'}}>
                            <LaunchIcon className={classes.icon} sx={{ fill: theme.colors.neutral['500'] }} />
                        </IconButton>
                    </Stack>
                )}
            </Stack>
        </Stack>
        {editModalOpen && (
            <SelectDealSourceModal
                open={editModalOpen}
                dashboard={dashboard}
                anchorElement={dealSourceRef.current}
                onClose={() => setEditModalOpen(false) } />
        )}
        {viewModalOpen && (
            <DealSourceModal
                open={viewModalOpen}
                dashboard={dashboard}
                anchorElement={dealSourceRef.current}
                onClose={() => setViewModalOpen(false)} />
        )}
    </>);
}

export default memo(SourceCell);
